import { CSSProperties } from 'react'
import { Helmet } from 'react-helmet-async'
import { PageMeta } from '@/components/PageMeta'
import { LandingButton, Reveal } from '@/ui/components/landing/primitives'
import { LandingNav } from '@/ui/components/landing/sections/LandingNav'
import { ClosingSection, LandingFooter } from '@/ui/components/landing/sections/ClosingSection'
import '@/ui/components/landing/landing.css'

const enterDelay = (ms: number) => ({ '--l-enter-delay': `${ms}ms` }) as CSSProperties

const SITREP_FIELDS = [
  { label: 'Route', value: '199 mi · Mojave → Twentynine Palms' },
  { label: 'Crew', value: 'Alex M., Priya R., Sam K., +1' },
  { label: 'Meals', value: 'Campfire Chili on Friday, Priya cooking' },
  { label: 'Gear', value: 'Sand ladders still need a rig' },
  { label: 'Radio', value: 'CB ch. 19' },
]

const SMALL_PHOTOS = [
  { src: '/about/canyon-camp-1100.jpg', width: 1100, height: 825, alt: 'Aerial view of a crew camped in a circle on a canyon floor' },
  { src: '/about/forest-river-1100.jpg', width: 1100, height: 824, alt: 'A rig parked by a river in a sunlit stand of tall pines' },
  { src: '/about/meetup-1100.jpg', width: 1100, height: 733, alt: 'A crew meeting up in a parking lot before heading out, rigs lined up under cloudy skies' },
  { src: '/about/river-bar-1100.jpg', width: 1100, height: 618, alt: 'Two rigs on a rocky river bar with bare trees on either side' },
]

const BUILDING_POINTS = [
  {
    name: 'The whole trip, not just the route',
    body: 'Routes, gear, meals and photos in one plan, instead of a line on a map and a thread to go with it.',
  },
  {
    name: 'Control without the chaos',
    body: 'You stay in charge of the plan, without the worry of missing a detail or losing track of what matters.',
  },
  {
    name: 'Shaped by the people using it',
    body: 'I plan my own trips with it, and what I learn from the community goes back into the app.',
  },
]

function AboutHero() {
  return (
    <section className="l-hero l-hero--about">
      <div className="l-hero__picture">
        <img
          src="/about/overlander-1920.jpg"
          srcSet="/about/overlander-1080.jpg 1080w, /about/overlander-1920.jpg 1920w"
          sizes="max(100vw, 133svh)"
          width={1920}
          height={1440}
          alt="An overlander sitting on the roof of a 4Runner beside a rooftop tent, looking out over a lake under an overcast sky"
          // @ts-expect-error fetchpriority is valid HTML but missing from React 18's img typings
          fetchpriority="high"
        />
      </div>

      <div className="l-frame l-hero__rail">
        <div className="lg:col-span-5 lg:col-start-8">
          <h1 className="l-hero-title l-enter" style={enterDelay(100)}>
            By an overlander.
            <br />
            For the crew.
          </h1>
          <p className="l-enter mt-4 max-w-[38ch] [text-wrap:pretty]" style={enterDelay(170)}>
            I'm a software engineer and a PNW overlander. I built SitRepp because I kept losing the plan in the group
            chat.
          </p>
          <div className="l-enter mt-5" style={enterDelay(240)}>
            <LandingButton to="/signup" tone="ink">
              Start planning free
            </LandingButton>
          </div>
        </div>
      </div>
    </section>
  )
}

function ProblemSection() {
  return (
    <section className="pt-[var(--l-section)]">
      <div className="l-frame grid gap-10 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <Reveal className="l-lead max-w-[26em]">
            <h2 className="inline font-normal">Group chats turn into chaos. </h2>
            <p className="l-soft inline">
              Details get buried, plans get repeated, and it's impossible to keep everyone aligned.
            </p>
          </Reveal>
          <Reveal as="p" delay={60} className="l-soft mt-6 max-w-[44ch]">
            I went through this on trip after trip. Eventually it was obvious we needed something built for how
            overlanders actually plan.
          </Reveal>
        </div>
        <Reveal delay={120} className="lg:col-span-4 lg:col-start-9">
          <img
            src="/about/group-chat-640.jpg"
            alt="Hands holding a phone with a long group chat on the screen"
            width={640}
            height={928}
            loading="lazy"
            className="aspect-square w-full max-w-[440px] object-cover object-[50%_55%] lg:max-w-none"
          />
        </Reveal>
      </div>
    </section>
  )
}

function OriginSection() {
  return (
    <section className="pt-[var(--l-section)]">
      <div className="l-frame">
        <Reveal>
          <img
            src="/about/meadow-1920.jpg"
            srcSet="/about/meadow-1080.jpg 1080w, /about/meadow-1920.jpg 1920w"
            sizes="(min-width: 1600px) 1488px, 94vw"
            alt="A line of rigs crossing a golden meadow below a jagged mountain"
            width={1920}
            height={1280}
            loading="lazy"
            className="aspect-[4/3] w-full object-cover lg:aspect-[2/1]"
          />
        </Reveal>

        <div className="mt-10 grid gap-6 lg:mt-14 lg:grid-cols-12 lg:gap-8">
          <Reveal as="h2" className="l-head lg:col-span-5">
            The gap wasn't gear.
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal as="p" delay={60} className="max-w-[56ch]">
              We have rooftop tents, bumpers, lighting and cooking setups for every build. What we didn't have were
              digital tools that fit the way crews talk and prepare.
            </Reveal>
            <Reveal as="p" delay={120} className="l-soft mt-4 max-w-[56ch]">
              I write software, so that's what I had to give back. I didn't want to build another map app. I wanted to
              make it easy to share trips, plan as a crew and bring people together.
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  )
}

function NameSection() {
  return (
    <section className="pt-[var(--l-section)]">
      <div className="l-frame grid gap-12 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-5">
          <Reveal as="h2" className="l-head">
            Why it's called SitRepp.
          </Reveal>
          <Reveal as="p" delay={60} className="l-soft mt-4 max-w-[44ch]">
            I named it after the situation report: a quick summary that gets everyone on the same page. That's the job
            here. Clear, simple updates on where things stand, so every trip feels organized, safe and connected.
          </Reveal>
        </div>

        {/* A trip's sitrep, using the same sample trip as the landing page */}
        <Reveal delay={120} className="bg-landing-surface p-5 sm:p-8 lg:col-span-6 lg:col-start-7 lg:self-start">
          <h3 className="font-medium">Mojave Spring Run</h3>
          <p className="l-soft l-small">Apr 12–15, 4 days</p>
          <dl className="mt-6 grid gap-x-8 gap-y-3 sm:grid-cols-[minmax(0,6em)_minmax(0,1fr)]">
            {SITREP_FIELDS.map((field) => (
              <div key={field.label} className="contents">
                <dt className="l-soft l-small mt-2 sm:mt-0 sm:leading-[1.65]">{field.label}</dt>
                <dd className="min-w-0">{field.value}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </section>
  )
}

function CrewsSection() {
  return (
    <section className="pt-[var(--l-section)]">
      <div className="l-frame">
        <Reveal as="h2" className="l-head">
          For the crew, and whoever joins next.
        </Reveal>
        <Reveal as="p" delay={60} className="l-soft mt-4 max-w-[52ch]">
          I built it for crews who want to stay organized without losing the fun of exploring, and for the people who
          welcome newcomers the way I was welcomed when I started.
        </Reveal>

        <Reveal delay={120} className="mt-10 grid gap-2 lg:mt-14 lg:grid-cols-12">
          <img
            src="/about/dusk-camp-1600.jpg"
            alt="A crew's rigs and rooftop tents spread across a hilltop clearing at dusk"
            width={1600}
            height={1198}
            loading="lazy"
            className="aspect-[4/3] h-full w-full object-cover lg:col-span-7"
          />
          <div className="grid grid-cols-2 gap-2 lg:col-span-5">
            {SMALL_PHOTOS.map((photo) => (
              <img
                key={photo.src}
                src={photo.src}
                alt={photo.alt}
                width={photo.width}
                height={photo.height}
                loading="lazy"
                className="aspect-[4/3] h-full w-full object-cover"
              />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  )
}

function BuildingSection() {
  return (
    <section className="pt-[var(--l-section)]">
      <div className="l-frame">
        <Reveal as="h2" className="l-head">
          What I'm building.
        </Reveal>
        <ul className="mt-10 grid gap-x-8 gap-y-8 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3">
          {BUILDING_POINTS.map((point, index) => (
            <Reveal as="li" key={point.name} delay={index * 60} className="max-w-[34ch]">
              <span className="font-medium">{point.name}</span>
              <span className="l-soft mt-1 block">{point.body}</span>
            </Reveal>
          ))}
        </ul>
        <Reveal as="p" delay={180} className="mt-10 lg:mt-14">
          <span className="font-medium">Tim Muangkeo</span>
          <span className="l-soft"> · Founder</span>
        </Reveal>
      </div>
    </section>
  )
}

export default function AboutUs() {
  return (
    // `.dark` scopes every dark: variant + inverted token to this page only, as on the landing page
    <div className="dark landing-theme relative min-h-svh">
      <PageMeta title="About" description="Learn about SitRepp.io — collaborative overlanding trip planning" />
      <Helmet>
        <link rel="preload" href="/fonts/switzer-var.woff2" as="font" type="font/woff2" crossOrigin="anonymous" />
      </Helmet>

      <LandingNav pricingHref="/pricing" />

      <main>
        <AboutHero />
        <ProblemSection />
        <OriginSection />
        <NameSection />
        <CrewsSection />
        <BuildingSection />

        <Reveal className="mt-[var(--l-section)]">
          <img
            src="/about/night-1920.jpg"
            srcSet="/about/night-1080.jpg 1080w, /about/night-1920.jpg 1920w"
            sizes="100vw"
            alt="A rooftop tent glowing orange under the Milky Way"
            width={1920}
            height={1280}
            loading="lazy"
            className="l-night"
          />
        </Reveal>
        <ClosingSection />
      </main>

      <LandingFooter />
    </div>
  )
}
